import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import PasswordInput from "@/components/PasswordInput";
import WhatsAppOtpDialog from "@/components/WhatsAppOtpDialog";
import { toast } from "sonner";
import { WhatsappLogo } from "@phosphor-icons/react";

const LOGO = process.env.REACT_APP_LOGO_URL;
const REMEMBER_KEY = "padel_remember_email";

export default function Login() {
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPcCode, setShowPcCode] = useState(false);
  const [pcCode, setPcCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [otpOpen, setOtpOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Pre-fill remembered email
  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) {
      setEmailOrPhone(saved);
      setRemember(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(emailOrPhone, password, pcCode.trim() || undefined);
      // Persist (or clear) remembered email
      if (remember) localStorage.setItem(REMEMBER_KEY, emailOrPhone);
      else localStorage.removeItem(REMEMBER_KEY);
      toast.success("Bienvenido!");
      if (pcCode.trim()) toast.success("Acceso a Padel Center activado", { description: "Ya eres parte del ecosistema." });
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6" data-testid="login-page">
      <div className="w-full max-w-md animate-fade-in">
        <Link to="/" className="flex items-center justify-center gap-3 mb-10">
          <img src={LOGO} alt="Logo" className="h-14 w-14 rounded-full" />
          <span className="font-['Barlow_Condensed'] font-black text-xl uppercase tracking-wider">
            Disfruta y Diviertete
          </span>
        </Link>

        <div className="bg-[#1A1A1A] border border-white/10 p-8" data-testid="login-form-container">
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-1">Iniciar Sesion</h1>
          <p className="text-sm text-gray-400 mb-8">Ingresa con tu email o telefono</p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 mb-6" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Email o Telefono</Label>
              <Input
                data-testid="login-email-input"
                type="text"
                value={emailOrPhone}
                onChange={(e) => setEmailOrPhone(e.target.value)}
                placeholder="tu@email.com o +52..."
                className="bg-[#0A0A0A] border-white/20 text-white h-12 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] placeholder:text-gray-600"
                required
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Contrasena</Label>
              <PasswordInput
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Tu contrasena"
                className="bg-[#0A0A0A] border-white/20 text-white h-12 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] placeholder:text-gray-600"
                required
              />
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide h-12"
            >
              {loading ? "Ingresando..." : "Ingresar"}
            </Button>

            {/* WhatsApp OTP alternative */}
            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
              <div className="relative flex justify-center"><span className="bg-[#141414] px-2 text-[10px] tracking-widest uppercase text-gray-500">o</span></div>
            </div>
            <Button
              type="button"
              onClick={() => setOtpOpen(true)}
              className="w-full bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/40 font-bold uppercase tracking-wide h-11"
              data-testid="login-whatsapp-btn"
            >
              <WhatsappLogo size={18} weight="fill" className="mr-2" /> Entrar con WhatsApp
            </Button>

            {/* Padel Center optional code */}
            <div className="pt-1 border-t border-white/5">
              {!showPcCode ? (
                <button
                  type="button"
                  onClick={() => setShowPcCode(true)}
                  className="text-[10px] tracking-[0.2em] uppercase text-[#CCFF00]/70 hover:text-[#CCFF00] font-bold mt-3 flex items-center gap-1.5 mx-auto"
                  data-testid="login-pc-toggle"
                >
                  🔒 ¿Eres parte de Padel Center? Ingresa el código
                </button>
              ) : (
                <div className="mt-3 space-y-2 p-3 bg-[#0A1A0A]/60 border border-[#CCFF00]/20 rounded">
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#CCFF00]">Código Padel Center</Label>
                    <button type="button" onClick={() => { setShowPcCode(false); setPcCode(""); }} className="text-[10px] text-gray-500 hover:text-white" data-testid="login-pc-cancel">✕</button>
                  </div>
                  <Input
                    type="password"
                    value={pcCode}
                    onChange={(e) => setPcCode(e.target.value)}
                    placeholder="Solicítalo al director del club"
                    className="bg-black border-[#CCFF00]/30 text-white text-sm tracking-wider"
                    data-testid="login-pc-code-input"
                  />
                  <p className="text-[9px] text-gray-500 italic">Opcional — solo si perteneces al ecosistema Padel Center.</p>
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none pt-1" data-testid="login-remember-wrapper">
              <Checkbox
                checked={remember}
                onCheckedChange={(v) => setRemember(!!v)}
                className="border-white/30 data-[state=checked]:bg-[#CCFF00] data-[state=checked]:border-[#CCFF00] data-[state=checked]:text-black"
                data-testid="login-remember-checkbox"
              />
              <span className="text-xs text-gray-400 tracking-wider uppercase">Recordar mi usuario en este dispositivo</span>
            </label>
          </form>

          <p className="text-xs text-gray-500 mt-4 text-center">
            <Link to="/forgot-password" className="text-gray-400 hover:text-[#CCFF00] uppercase tracking-wider" data-testid="login-forgot-link">
              Olvidaste tu contrasena?
            </Link>
          </p>
          <p className="text-sm text-gray-500 mt-4 text-center">
            No tienes cuenta?{" "}
            <Link to="/register" className="text-[#CCFF00] hover:underline font-semibold" data-testid="login-register-link">
              Registrate
            </Link>
          </p>
        </div>
      </div>
      <WhatsAppOtpDialog open={otpOpen} onOpenChange={setOtpOpen} />
    </div>
  );
}
